import { useState } from "react";
import styled from "styled-components";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Form } from "../../components/Form";
import { FormGroup } from "../../components/FormGroup";
import { FormLabel } from "../../components/FormLabel";
import { FormActions } from "../../components/FormActions";
import { SelectInput } from "../../components/SelectInput";
import { Card } from "../../components/Card";
import { spacing } from "@styling/sizing";
import colors from "@styling/colors";
import { fontSizes } from "@styling/fonts";
import type {
  RestAvailableBillingItem,
} from "@KassaroAB/shared/types/availableBillingItem";
import { Currency } from "@KassaroAB/shared/types/enums/currency";
import type { MultiCurrencyMoney } from "@KassaroAB/shared/types/multiCurrencyMoney";
import { zeroMultiCurrencyMoney } from "@KassaroAB/shared/utils/multiCurrencyMoney";
import { formatMoney } from "../../utils/formatMoney";
import type { ResellerAvailableBillingItem, RestCreateResellerAvailableBillingItem } from "@KassaroAB/shared/types/resellerAvailableBillingItem";
import { useTranslation } from "react-i18next";

const PriceInputGroup = styled.div`
  display: flex;
  gap: ${spacing.x2}px;
  align-items: flex-end;
  margin-bottom: ${spacing.x2}px;
`;

const PriceInput = styled(Input)`
  flex: 1;
`;

const CurrencySelect = styled(SelectInput)`
  width: 100px;
`;

const PriceLabel = styled(FormLabel)`
  font-weight: 600;
  margin-bottom: ${spacing.x1}px;
`;

const ItemCard = styled(Card)`
  cursor: pointer;
  transition: background-color 0.2s;
  margin-bottom: ${spacing.x2}px;

  &:hover {
    background-color: ${colors.backgroundDark};
  }
`;

const ItemName = styled.div`
  font-size: ${fontSizes.large}px;
  font-weight: 600;
  color: ${colors.text};
  margin-bottom: ${spacing.x1}px;
`;

const ItemDescription = styled.div`
  font-size: ${fontSizes.regular}px;
  color: ${colors.textMuted};
  margin-bottom: ${spacing.x1}px;
`;

const ItemType = styled.div`
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
  text-transform: uppercase;
`;

const StepTitle = styled.h3`
  font-size: ${fontSizes.large}px;
  font-weight: 600;
  color: ${colors.text};
  margin-bottom: ${spacing.x3}px;
`;

interface ResellerAvailableBillingItemFormProps {
  availableBillingItems: RestAvailableBillingItem[];
  resellerAvailableBillingItem?: ResellerAvailableBillingItem;
  onSubmit: (data: RestCreateResellerAvailableBillingItem) => void;
  onCancel: () => void;
}

const arePricesEqual = (price1?: MultiCurrencyMoney, price2?: MultiCurrencyMoney): boolean => {
  if (!price1 && !price2) return true;
  if (!price1 || !price2) return false;
  
  const currencies1 = price1.currencies || {};
  const currencies2 = price2.currencies || {};
  
  const allCurrencies = new Set([...Object.keys(currencies1), ...Object.keys(currencies2)]);
  
  for (const currency of allCurrencies) {
    const amount1 = currencies1[currency as Currency]?.amount || 0;
    const amount2 = currencies2[currency as Currency]?.amount || 0;
    if (amount1 !== amount2) return false;
  }
  
  return true;
};

export const ResellerAvailableBillingItemForm = ({
  availableBillingItems,
  resellerAvailableBillingItem,
  onSubmit,
  onCancel,
}: ResellerAvailableBillingItemFormProps) => {
  const { t } = useTranslation();
  const formatPricing = (pricing: RestAvailableBillingItem["pricing"]): string => {
    const prices: string[] = [];
    if (pricing.monthlyPrice) {
      const formatted = Object.values(pricing.monthlyPrice.currencies || {})
        .filter(Boolean)
        .map((money) => formatMoney(money!))
        .join(", ");
      if (formatted) {
        prices.push(t("resellerAvailableBillingItemForm.pricingMonthly", { formatted }));
      }
    }
    if (pricing.oneTimePrice) {
      const formatted = Object.values(pricing.oneTimePrice.currencies || {})
        .filter(Boolean)
        .map((money) => formatMoney(money!))
        .join(", ");
      if (formatted) {
        prices.push(t("resellerAvailableBillingItemForm.pricingOneTime", { formatted }));
      }
    }
    if (pricing.prepaid36MonthPrice) {
      const formatted = Object.values(pricing.prepaid36MonthPrice.currencies || {})
        .filter(Boolean)
        .map((money) => formatMoney(money!))
        .join(", ");
      if (formatted) {
        prices.push(t("resellerAvailableBillingItemForm.pricing36mo", { formatted }));
      }
    }
    return prices.length > 0 ? prices.join(" | ") : "-";
  };
  const [step, setStep] = useState<"select" | "edit">(resellerAvailableBillingItem ? "edit" : "select");
  const [selectedAvailableBillingItemId, setSelectedAvailableBillingItemId] = useState<string | null>(
    resellerAvailableBillingItem?.id || null
  );
  
  const selectedItem = availableBillingItems.find(item => item.id === selectedAvailableBillingItemId);
  
  const getDefaultPrices = () => {
    if (resellerAvailableBillingItem) {
      const originalItem = availableBillingItems.find(item => item.id === resellerAvailableBillingItem.id);
      return {
        monthly: originalItem?.pricing?.monthlyPrice || zeroMultiCurrencyMoney(),
        oneTime: originalItem?.pricing?.oneTimePrice || zeroMultiCurrencyMoney(),
        prepaid36: originalItem?.pricing?.prepaid36MonthPrice || zeroMultiCurrencyMoney(),
      };
    }
    return {
      monthly: selectedItem?.pricing?.monthlyPrice || zeroMultiCurrencyMoney(),
      oneTime: selectedItem?.pricing?.oneTimePrice || zeroMultiCurrencyMoney(),
      prepaid36: selectedItem?.pricing?.prepaid36MonthPrice || zeroMultiCurrencyMoney(),
    };
  };

  const defaultPrices = getDefaultPrices();

  const [monthlyPrice, setMonthlyPrice] = useState<MultiCurrencyMoney>(
    resellerAvailableBillingItem?.pricing?.monthlyPrice || 
    defaultPrices.monthly,
  );
  const [oneTimePrice, setOneTimePrice] = useState<MultiCurrencyMoney>(
    resellerAvailableBillingItem?.pricing?.oneTimePrice || 
    defaultPrices.oneTime,
  );
  const [prepaid36MonthPrice, setPrepaid36MonthPrice] = useState<MultiCurrencyMoney>(
    resellerAvailableBillingItem?.pricing?.prepaid36MonthPrice || 
    defaultPrices.prepaid36,
  );

  const handlePriceChange = (
    priceType: "monthly" | "oneTime" | "prepaid36",
    currency: string,
    amountDisplay: string,
  ) => {
    const amountCents = Math.round((parseFloat(amountDisplay) || 0) * 100);
    const priceUpdate = {
      currencies: {
        ...(priceType === "monthly" ? monthlyPrice : priceType === "oneTime" ? oneTimePrice : prepaid36MonthPrice).currencies,
        [currency]: {
          amount: amountCents,
          currency,
        },
      },
    };

    if (priceType === "monthly") {
      setMonthlyPrice(priceUpdate);
    } else if (priceType === "oneTime") {
      setOneTimePrice(priceUpdate);
    } else {
      setPrepaid36MonthPrice(priceUpdate);
    }
  };

  const handleItemSelect = (itemId: string) => {
    setSelectedAvailableBillingItemId(itemId);
    const item = availableBillingItems.find(i => i.id === itemId);
    if (item) {
      setMonthlyPrice(item.pricing?.monthlyPrice || zeroMultiCurrencyMoney());
      setOneTimePrice(item.pricing?.oneTimePrice || zeroMultiCurrencyMoney());
      setPrepaid36MonthPrice(item.pricing?.prepaid36MonthPrice || zeroMultiCurrencyMoney());
      setStep("edit");
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAvailableBillingItemId && !resellerAvailableBillingItem) return;

    const currentDefaultPrices = getDefaultPrices();

    const pricing: Record<string, MultiCurrencyMoney> = {};
    
    if (!arePricesEqual(monthlyPrice, currentDefaultPrices.monthly)) {
      pricing.monthlyPrice = monthlyPrice;
    }
    if (!arePricesEqual(oneTimePrice, currentDefaultPrices.oneTime)) {
      pricing.oneTimePrice = oneTimePrice;
    }
    if (!arePricesEqual(prepaid36MonthPrice, currentDefaultPrices.prepaid36)) {
      pricing.prepaid36MonthPrice = prepaid36MonthPrice;
    }

    if (resellerAvailableBillingItem) {
      if (Object.keys(pricing).length > 0) {
        onSubmit({ pricing, id: resellerAvailableBillingItem.id });
      } else {
        onSubmit({ id: resellerAvailableBillingItem.id });
      }
    } else {
      if (Object.keys(pricing).length > 0) {
        onSubmit({
          pricing,
          id: selectedAvailableBillingItemId!,
        });
      } else {
        onSubmit({
          id: selectedAvailableBillingItemId!,
        });
      }
    }
  };

  if (step === "select") {
    return (
      <Form onSubmit={(e) => e.preventDefault()}>
        {availableBillingItems.length === 0 ? (
          <div style={{ color: colors.textMuted, textAlign: "center", padding: spacing.x4 }}>
            {t("resellerAvailableBillingItemForm.emptySelect")}
          </div>
        ) : (
          availableBillingItems.map((item) => (
            <ItemCard
              key={item.id}
              onClick={() => handleItemSelect(item.id)}
            >
              <ItemName>{item.name}</ItemName>
              <ItemDescription>{item.description}</ItemDescription>
              <ItemType>{item.type}</ItemType>
              <div style={{ marginTop: spacing.x1, fontSize: fontSizes.small, color: colors.textMuted }}>
                {t("resellerAvailableBillingItemForm.defaultPricing", {
                  pricing: formatPricing(item.pricing),
                })}
              </div>
            </ItemCard>
          ))
        )}
        <FormActions>
          <Button type="button" $color="secondary" onClick={onCancel}>
            {t("common.cancel")}
          </Button>
        </FormActions>
      </Form>
    );
  }

  return (
    <Form onSubmit={handleSubmit}>
      <StepTitle>
        {t("resellerAvailableBillingItemForm.editPricingTitle", { name: selectedItem?.name ?? "" })}
      </StepTitle>
      
      {selectedItem?.description && (
        <FormGroup>
          <div style={{ color: colors.textMuted, fontSize: fontSizes.regular }}>
            {selectedItem.description}
          </div>
        </FormGroup>
      )}

      <FormGroup>
        <PriceLabel>{t("resellerAvailableBillingItemForm.monthlyOptional")}</PriceLabel>
        {Object.values(Currency).map((currency) => (
          <PriceInputGroup key={currency}>
            <PriceInput
              type="number"
              step="0.01"
              placeholder={t("common.amount")}
              value={monthlyPrice.currencies[currency]?.amount != null ? monthlyPrice.currencies[currency].amount / 100 : ""}
              onChange={(e) => handlePriceChange("monthly", currency, e.target.value)}
            />
            <CurrencySelect value={currency} disabled>
              <option value={currency}>{currency}</option>
            </CurrencySelect>
          </PriceInputGroup>
        ))}
      </FormGroup>

      <FormGroup>
        <PriceLabel>{t("resellerAvailableBillingItemForm.oneTimeOptional")}</PriceLabel>
        {Object.values(Currency).map((currency) => (
          <PriceInputGroup key={currency}>
            <PriceInput
              type="number"
              step="0.01"
              placeholder={t("common.amount")}
              value={oneTimePrice.currencies[currency]?.amount != null ? oneTimePrice.currencies[currency].amount / 100 : ""}
              onChange={(e) => handlePriceChange("oneTime", currency, e.target.value)}
            />
            <CurrencySelect value={currency} disabled>
              <option value={currency}>{currency}</option>
            </CurrencySelect>
          </PriceInputGroup>
        ))}
      </FormGroup>

      <FormGroup>
        <PriceLabel>{t("resellerAvailableBillingItemForm.prepaid36Optional")}</PriceLabel>
        {Object.values(Currency).map((currency) => (
          <PriceInputGroup key={currency}>
            <PriceInput
              type="number"
              step="0.01"
              placeholder={t("common.amount")}
              value={prepaid36MonthPrice.currencies[currency]?.amount != null ? prepaid36MonthPrice.currencies[currency].amount / 100 : ""}
              onChange={(e) => handlePriceChange("prepaid36", currency, e.target.value)}
            />
            <CurrencySelect value={currency} disabled>
              <option value={currency}>{currency}</option>
            </CurrencySelect>
          </PriceInputGroup>
        ))}
      </FormGroup>

      <FormActions>
        <Button type="button" $color="secondary" onClick={() => setStep("select")}>
          {t("common.back")}
        </Button>
        <Button type="button" $color="secondary" onClick={onCancel}>
          {t("common.cancel")}
        </Button>
        <Button type="submit">
          {resellerAvailableBillingItem ? t("common.update") : t("resellerAvailableBillingItemForm.add")}
        </Button>
      </FormActions>
    </Form>
  );
};
